import React from 'react';

import classNames from 'classnames';

import { formatRate } from './fxFormat';
import { ReviewNote } from './FxRatesTable';

import { formatDateLong } from 'Helpers/dateFormat';

import Badge from 'CommonComponents/Badge';
import Button from 'CommonComponents/Button';
import SegmentedControl from 'CommonComponents/SegmentedControl';

import LockIcon from 'Assets/icons/lock-closed.svg';

const MODE_COMMON = 'common';
const MODE_LOCKED = 'locked';

function RateApplied({ project }) {
    if (project.is_anchor) {
        return <span className="tw-text-charcoal-50">1.0000</span>;
    }

    if (project.missing) {
        return (
            <Badge variant="warning" size="md">
                {$t('No rate')}
            </Badge>
        );
    }

    if (project.mode === MODE_LOCKED) {
        return (
            <span title={project.reason || ''}>
                <Badge variant="info" size="md" icon={<LockIcon />}>
                    {formatRate(project.rate)}
                </Badge>
            </span>
        );
    }

    return formatRate(project.rate);
}

function reasonText(project) {
    if (project.is_anchor) {
        return $t('Anchor currency');
    }

    if (!project.currency) {
        return $t('No currency set on the project');
    }

    return project.mode === MODE_LOCKED ? project.reason : $t('Follows common rate');
}

/**
 * Every project with the rate it is converted at. A project follows its
 * currency's common rate unless locked; one in the anchor currency is always
 * 1.0000 and cannot be locked.
 */
export default function FxProjectsTable({ projects, onLock, onUnlock, onReview }) {
    const modeOptions = [
        { value: MODE_COMMON, label: $t('Common') },
        { value: MODE_LOCKED, label: $t('Locked') },
    ];

    return (
        <div className="table__card">
            <div className="tw-mb-4">
                <h2 className="tw-text-blue tw-text-xl tw-font-semibold tw-mb-1">{$t('Project rates')}</h2>
                <p className="tw-text-sm tw-font-medium tw-text-charcoal-50 tw-mb-0">
                    {$t(
                        'Every project follows the common rate unless it is locked. A locked rate stays fixed when the common rate moves, for example a hedged or historical rate.',
                    )}
                </p>
            </div>

            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th className="!tw-text-start">{$t('Project')}</th>
                            <th className="!tw-text-start">{$t('Base currency')}</th>
                            <th className="!tw-text-start">{$t('Mode')}</th>
                            <th className="!tw-text-end">{$t('Rate applied')}</th>
                            <th className="!tw-text-start">{$t('As at')}</th>
                            <th className="!tw-text-start">{$t('Reason')}</th>
                            <th>
                                <span className="tw-sr-only">{$t('Actions')}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {projects.length === 0 && (
                            <tr>
                                <td colSpan={7} className="tw-text-center tw-text-charcoal-75">
                                    {$t('No projects yet.')}
                                </td>
                            </tr>
                        )}
                        {projects.map((project) => {
                            const lockable = !project.is_anchor && !!project.currency;

                            return (
                                <tr key={project.id} className={classNames({ 'tw-text-charcoal-50': project.is_anchor })}>
                                    <td className="tw-text-start tw-font-semibold">
                                        {project.name}
                                        {project.needs_review && (
                                            <ReviewNote noteKey={project.note_key} noteParams={project.note_params} onReview={() => onReview(project)} />
                                        )}
                                    </td>
                                    <td className="tw-text-start">
                                        {project.currency ? (
                                            <Badge variant={project.is_anchor ? 'info' : 'neutral'} size="md">
                                                {project.currency}
                                            </Badge>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                    <td className="tw-text-start">
                                        <SegmentedControl
                                            variant="track"
                                            options={modeOptions}
                                            value={project.mode}
                                            disabled={!lockable}
                                            onChange={(mode) => {
                                                if (mode === MODE_LOCKED && project.mode !== MODE_LOCKED) {
                                                    onLock(project);
                                                } else if (mode === MODE_COMMON && project.mode === MODE_LOCKED) {
                                                    onUnlock(project);
                                                }
                                            }}
                                        />
                                    </td>
                                    <td className="inbud-numeric-cell">
                                        <RateApplied project={project} />
                                    </td>
                                    <td className="tw-text-start tw-text-sm">{project.is_anchor ? '' : formatDateLong(project.as_at)}</td>
                                    <td className="tw-text-start tw-text-sm">{reasonText(project)}</td>
                                    <td className="tw-text-end">
                                        {project.mode === MODE_LOCKED && (
                                            <Button variant="secondary" small onClick={() => onLock(project)}>
                                                {$t('Edit')}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
